import { Base } from '@studiometa/js-toolkit';
import { gsap } from 'gsap';

// Délai (ms) avant que le header ne glisse depuis le haut du site.
const REVEAL_DELAY = 2600;

/**
 * @typedef {{
 *   $refs: {
 *     burger: HTMLElement,
 *     nav: HTMLElement,
 *     language: HTMLElement,
 *     languageToggle: HTMLButtonElement,
 *     languageMenu: HTMLElement,
 *     navLink: HTMLAnchorElement[],
 *   }
 * }} HeaderProps
 */

/**
 * @augments {Base<HeaderProps>}
 */
export default class Header extends Base {
  static config = {
    name: 'Header',
    refs: ['burger', 'nav', 'language', 'languageToggle', 'languageMenu', 'navLink[]'],
    log: true,
  };

  /**
   * Close the language dropdown when clicking outside of it.
   * @param {MouseEvent} event
   */
  onDocumentClick = (event) => {
    if (this.$refs.language && !this.$refs.language.contains(/** @type {Node} */ (event.target))) {
      this.closeLanguage();
    }
  };

  /**
   * @type {number}
   */
  revealTimeout = 0;

  /**
   * Bind the outside click listener et fait glisser le header depuis le haut du
   * site après un délai, via la classe `is-revealed` (transition CSS). Le header
   * est masqué (translaté vers le haut) en CSS avant le JS pour éviter tout
   * flash ; en `prefers-reduced-motion` le CSS le laisse en place et on
   * l'affiche immédiatement.
   */
  mounted() {
    document.addEventListener('click', this.onDocumentClick);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.$el.classList.add('is-revealed');
      return;
    }

    this.revealTimeout = window.setTimeout(() => {
      this.$el.classList.add('is-revealed');
    }, REVEAL_DELAY);
  }

  /**
   * Remove the outside click listener et annule le timeout de révélation.
   */
  destroyed() {
    document.removeEventListener('click', this.onDocumentClick);
    window.clearTimeout(this.revealTimeout);
  }

  /**
   *
   */
  onBurgerClick() {
    const isOpening = !this.$el.classList.contains('is-open');
    this.$el.classList.toggle('is-open');
    this.$refs.burger.classList.toggle('is-open');
    this.$refs.nav.classList.toggle('is-open');

    if (isOpening) {
      gsap.fromTo(
        this.$refs.navLink,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          ease: 'none',
          stagger: 0.075,
        },
      );
    } else {
      gsap.set(this.$refs.navLink, { clearProps: 'all' });
    }
  }

  /**
   * Close the mobile menu when a nav link is clicked.
   */
  onNavLinkClick() {
    if (this.$el.classList.contains('is-open')) {
      this.$el.classList.remove('is-open');
      this.$refs.burger.classList.remove('is-open');
      this.$refs.nav.classList.remove('is-open');
    }
  }

  /**
   * Toggle the language dropdown.
   */
  onLanguageToggleClick() {
    if (this.$refs.languageMenu.classList.contains('is-open')) {
      this.closeLanguage();
    } else {
      this.openLanguage();
    }
  }

  /**
   * Open the language dropdown.
   */
  openLanguage() {
    this.$refs.languageMenu.classList.add('is-open');
    this.$refs.languageToggle.setAttribute('aria-expanded', 'true');
  }

  /**
   * Close the language dropdown.
   */
  closeLanguage() {
    this.$refs.languageMenu.classList.remove('is-open');
    this.$refs.languageToggle.setAttribute('aria-expanded', 'false');
  }
}
